import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  AlertTriangle,
  Loader2,
  RotateCcw,
  RotateCw,
  Settings,
} from 'lucide-react';

// Единый плеер на всё приложение: каталог, страница обучения, админ-превью.
// Защита от тривиального скачивания: controlsList="nodownload",
// контекстное меню заблокировано, drag отключён, нативные controls не используются.
// Полностью защитить видео в браузере невозможно (это HLS/DRM-территория),
// но эти меры отсекают 95% сценариев "правый клик → сохранить".

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
const RATE_STORAGE_KEY = 'uaqyt:playerRate';

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60).toString().padStart(2, '0');
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const mm = (m % 60).toString().padStart(2, '0');
    return `${h}:${mm}:${s}`;
  }
  return `${m}:${s}`;
};

const VideoPlayer = ({
  videoUrl,
  posterUrl,
  title,
  watermark,
  className = '',
  autoPlay = false,
  onTimeUpdate,
  onDurationChange,
  onEnded,
  onPlay,
  onPause,
}) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const hideTimerRef = useRef(null);

  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [progress, setProgress] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [rate, setRate] = useState(() => {
    const saved = parseFloat(localStorage.getItem(RATE_STORAGE_KEY) || '1');
    return PLAYBACK_RATES.includes(saved) ? saved : 1;
  });
  const [showRateMenu, setShowRateMenu] = useState(false);
  // Натуральные пропорции видео — определяем по metadata, чтобы портретные
  // ролики (9:16, снятые на телефон) не уезжали в чёрные поля внутри 16:9.
  // Дефолт 16/9 — для лендсейп-видео и до загрузки metadata.
  const [aspectRatio, setAspectRatio] = useState('16 / 9');

  // ─── Жизненный цикл видео ─────────────────────
  useEffect(() => {
    setError(false);
    setLoading(true);
    setProgress(0);
    setBuffered(0);
    setDuration(0);
  }, [videoUrl]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.playbackRate = rate;
  }, [rate, videoUrl]);

  // ─── Автоскрытие контролов ─────────────────────
  const scheduleHide = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      if (videoRef.current && !videoRef.current.paused) setShowControls(false);
    }, 2500);
  }, []);

  const wakeControls = useCallback(() => {
    setShowControls(true);
    scheduleHide();
  }, [scheduleHide]);

  useEffect(() => () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  }, []);

  // ─── Fullscreen sync ─────────────────────
  useEffect(() => {
    const handler = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // ─── Управление ─────────────────────
  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, []);

  const toggleMute = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  }, []);

  const handleVolume = (e) => {
    const v = videoRef.current;
    if (!v) return;
    const value = Number(e.target.value);
    v.volume = value;
    v.muted = value === 0;
    setVolume(value);
    setMuted(value === 0);
  };

  const handleSeek = (e) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    v.currentTime = ratio * duration;
    setProgress(ratio * 100);
  };

  const skip = (delta) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    v.currentTime = Math.min(duration, Math.max(0, v.currentTime + delta));
  };

  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current;
    const video = videoRef.current;
    if (!container || !video) return;

    // iOS Safari НЕ поддерживает стандартный Fullscreen API на контейнере —
    // requestFullscreen на div'е просто молча ничего не делает. Зато у
    // <video> есть webkitEnterFullscreen() — это нативный фуллскрин-плеер
    // iOS со своими контролами. Соглашаемся отдать управление системе:
    // на iPhone лучше работают native controls, чем наши кастомные.
    if (typeof video.webkitEnterFullscreen === 'function' && !document.fullscreenElement) {
      try {
        video.webkitEnterFullscreen();
        return;
      } catch {
        // прокидываем дальше к стандартному пути
      }
    }

    if (!document.fullscreenElement) {
      container.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  }, []);

  const changeRate = (newRate) => {
    const v = videoRef.current;
    if (!v) return;
    v.playbackRate = newRate;
    setRate(newRate);
    localStorage.setItem(RATE_STORAGE_KEY, String(newRate));
    setShowRateMenu(false);
  };

  // ─── Хоткеи ─────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (!containerRef.current?.contains(document.activeElement) && document.activeElement !== document.body) {
        return;
      }
      if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) return;
      if (e.key === ' ' || e.key === 'k') { e.preventDefault(); togglePlay(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); skip(-5); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); skip(5); }
      else if (e.key === 'm') { e.preventDefault(); toggleMute(); }
      else if (e.key === 'f') { e.preventDefault(); toggleFullscreen(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePlay, toggleMute, toggleFullscreen]);

  // ─── Рендер пустых/ошибочных состояний ─────────────────────
  if (!videoUrl) {
    return (
      <div className={`aspect-video flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-2xl ${className}`}>
        <div className="text-center p-6">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-2" />
          <p className="text-gray-600 dark:text-gray-400">Видео недоступно</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`aspect-video flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-2xl ${className}`}>
        <div className="text-center p-6">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-2" />
          <p className="text-gray-600 dark:text-gray-400">Не удалось загрузить видео</p>
        </div>
      </div>
    );
  }

  const progressPercent = duration ? (progress / 100) * 100 : 0;
  const bufferedPercent = duration ? Math.min(100, (buffered / duration) * 100) : 0;

  return (
    <div
      ref={containerRef}
      // В фуллскрине убираем aspect-video (форсирует 16:9 → видео не
      // заполняет экран, остаются чёрные полосы сверху/снизу). Браузер
      // сам растягивает контейнер на :fullscreen → даём ему свободу через
      // h-full. Тогда object-contain на video правильно вписывает кадр.
      // Также убираем rounded-2xl и shadow в FS — это нужно только в карточке.
      className={`group relative bg-black overflow-hidden select-none ${
        isFullscreen
          ? 'h-full w-full'
          : 'rounded-2xl shadow-xl mx-auto'
      } ${className}`}
      style={
        isFullscreen
          ? undefined
          : {
              // Применяем aspect-ratio от самого видео — портрет не уйдёт
              // в чёрные поля 16:9. max-height ограничивает портрет, чтобы он
              // не занимал всю высоту экрана и оставлял место под контент.
              aspectRatio,
              maxHeight: '70vh',
            }
      }
      onMouseMove={wakeControls}
      onMouseLeave={() => playing && setShowControls(false)}
      onContextMenu={(e) => e.preventDefault()}
    >
      <video
        ref={videoRef}
        src={videoUrl}
        poster={posterUrl}
        autoPlay={autoPlay}
        className="w-full h-full object-contain pointer-events-none"
        playsInline
        controlsList="nodownload noplaybackrate noremoteplayback"
        disablePictureInPicture
        disableRemotePlayback
        onClick={togglePlay}
        onContextMenu={(e) => e.preventDefault()}
        onLoadedMetadata={(e) => {
          const v = e.currentTarget;
          const d = v.duration;
          setDuration(d);
          onDurationChange?.(d);
          // Меняем aspect-ratio контейнера только если у видео есть
          // валидные размеры. Если 0×0 (битый файл) — оставляем 16:9.
          if (v.videoWidth > 0 && v.videoHeight > 0) {
            setAspectRatio(`${v.videoWidth} / ${v.videoHeight}`);
          }
        }}
        onTimeUpdate={(e) => {
          const cur = e.currentTarget.currentTime;
          const dur = e.currentTarget.duration || 1;
          setProgress((cur / dur) * 100);
          onTimeUpdate?.(cur, dur);
        }}
        onProgress={(e) => {
          const v = e.currentTarget;
          if (v.buffered.length) setBuffered(v.buffered.end(v.buffered.length - 1));
        }}
        onWaiting={() => setLoading(true)}
        // iOS Safari нестабильно стреляет `canplay`/`canplaythrough` — поэтому
        // дублируем "видео готово" через loadeddata (первый кадр) и playing
        // (когда буфер реально пошёл). Без этого спиннер прилипает поверх
        // уже играющего видео.
        onLoadedData={() => setLoading(false)}
        onCanPlay={() => setLoading(false)}
        onCanPlayThrough={() => setLoading(false)}
        onPlaying={() => setLoading(false)}
        onPlay={() => { setPlaying(true); setLoading(false); scheduleHide(); onPlay?.(); }}
        onPause={() => { setPlaying(false); setShowControls(true); onPause?.(); }}
        onEnded={() => { setPlaying(false); setShowControls(true); onEnded?.(); }}
        onError={() => setError(true)}
        onVolumeChange={(e) => {
          setMuted(e.currentTarget.muted);
          setVolume(e.currentTarget.volume);
        }}
        draggable={false}
        title={title}
      >
        Ваш браузер не поддерживает воспроизведение видео.
      </video>

      {/* Прозрачный слой клика для play/pause (вся область видео).
          Двойной клик — fullscreen. */}
      <button
        type="button"
        aria-label={playing ? 'Pause' : 'Play'}
        className="absolute inset-0 z-10 cursor-pointer"
        onClick={togglePlay}
        onDoubleClick={toggleFullscreen}
      />

      {/* Watermark — едва заметная подпись email/имени; деморализует прямую ретрансляцию */}
      {watermark && (
        <div className="pointer-events-none absolute right-3 top-3 z-20 rounded-full bg-black/30 backdrop-blur px-3 py-1 text-[11px] font-medium text-white/70">
          {watermark}
        </div>
      )}

      {/* Loader — показываем ТОЛЬКО если активно играем и буферимся.
          На паузе никакого спиннера: если loading прилип в true (iOS
          не стрельнул canplay), play-кнопка всё равно видна и юзер
          может ткнуть. После первого взаимодействия события дострелят
          и loading чистится. */}
      {loading && playing && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/30 pointer-events-none">
          <Loader2 className="w-12 h-12 text-white/90 animate-spin" />
        </div>
      )}

      {/* Большой Play по центру — когда на паузе. Не блокируем на loading,
          иначе на iOS юзер залипает с вечным спиннером и без play-кнопки. */}
      {!playing && (
        <button
          type="button"
          onClick={togglePlay}
          className="absolute inset-0 z-20 flex items-center justify-center"
          aria-label="Play"
        >
          <span className="rounded-full bg-white/95 p-5 shadow-2xl transition-transform hover:scale-105">
            <Play className="w-10 h-10 text-gray-900 fill-gray-900" />
          </span>
        </button>
      )}

      {/* Title — слева сверху */}
      {title && (
        <div
          className={`absolute left-3 top-3 z-20 max-w-[60%] rounded-lg bg-black/40 backdrop-blur px-3 py-1.5 text-sm text-white transition-opacity ${
            showControls || !playing ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <p className="truncate">{title}</p>
        </div>
      )}

      {/* Контролы. onClick с проверкой target === currentTarget — клик
          по ПУСТОЙ области контрол-бара тоже пуазит видео (как в YouTube).
          Клик по кнопкам/слайдеру не триггерит pause (e.target ≠ container). */}
      <div
        onClick={(e) => {
          if (e.target === e.currentTarget) togglePlay();
        }}
        className={`absolute inset-x-0 bottom-0 z-30 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-3 pt-8 pb-3 transition-opacity duration-200 ${
          showControls || !playing ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Прогресс-бар */}
        <div
          className="group/seek relative mb-2 h-1.5 cursor-pointer rounded-full bg-white/20"
          onClick={handleSeek}
        >
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-white/30"
            style={{ width: `${bufferedPercent}%` }}
          />
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-indigo-500"
            style={{ width: `${progressPercent}%` }}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white opacity-0 group-hover/seek:opacity-100 transition-opacity"
            style={{ left: `calc(${progressPercent}% - 6px)` }}
          />
        </div>

        <div className="flex items-center justify-between gap-2 text-white">
          {/* Левая группа — flex-1 min-w-0 чтобы могла сжаться,
              правая группа shrink-0 чтобы fullscreen ВСЕГДА был виден. */}
          <div className="flex items-center gap-0.5 sm:gap-1 min-w-0">
            <button onClick={togglePlay} className="p-2 hover:bg-white/15 rounded-lg shrink-0" aria-label={playing ? 'Pause' : 'Play'}>
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>
            {/* Skip ±10 — только на sm+. На мобиле сжирают место и юзер
                всё равно прыгает свайпом по таймлайну. */}
            <button onClick={() => skip(-10)} className="hidden sm:inline-flex p-2 hover:bg-white/15 rounded-lg shrink-0" aria-label="Назад 10с">
              <RotateCcw className="w-5 h-5" />
            </button>
            <button onClick={() => skip(10)} className="hidden sm:inline-flex p-2 hover:bg-white/15 rounded-lg shrink-0" aria-label="Вперёд 10с">
              <RotateCw className="w-5 h-5" />
            </button>

            {/* Volume — только на широких плеерах (lg+), иначе пожирает место */}
            <div className="hidden lg:flex items-center gap-2 ml-1 shrink-0">
              <button onClick={toggleMute} className="p-2 hover:bg-white/15 rounded-lg" aria-label={muted ? 'Unmute' : 'Mute'}>
                {muted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={muted ? 0 : volume}
                onChange={handleVolume}
                className="w-20 accent-indigo-500"
                aria-label="Громкость"
              />
            </div>
            {/* Mute toggle для sm-lg (планшетов). На мобиле hidden — место экономим. */}
            <button onClick={toggleMute} className="hidden sm:inline-flex lg:hidden p-2 hover:bg-white/15 rounded-lg shrink-0" aria-label={muted ? 'Unmute' : 'Mute'}>
              {muted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>

            <span className="ml-1 sm:ml-2 text-[10px] sm:text-xs tabular-nums text-white/85 truncate">
              {formatTime((progress / 100) * duration)} / {formatTime(duration)}
            </span>
          </div>

          {/* Правая группа — shrink-0 → НИКОГДА не вытесняется */}
          <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
            <div className="relative hidden sm:block">
              <button
                onClick={() => setShowRateMenu((s) => !s)}
                className="px-2 py-1.5 hover:bg-white/15 rounded-lg text-xs font-semibold tabular-nums flex items-center gap-1"
                aria-label="Скорость"
              >
                <Settings className="w-4 h-4" />
                {rate}×
              </button>
              {showRateMenu && (
                <div className="absolute bottom-full right-0 mb-2 rounded-xl bg-black/90 backdrop-blur ring-1 ring-white/10 py-1 min-w-[80px]">
                  {PLAYBACK_RATES.map((r) => (
                    <button
                      key={r}
                      onClick={() => changeRate(r)}
                      className={`block w-full px-3 py-1.5 text-xs text-left hover:bg-white/10 ${
                        rate === r ? 'text-indigo-300 font-semibold' : 'text-white'
                      }`}
                    >
                      {r}×
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button onClick={toggleFullscreen} className="p-2 hover:bg-white/15 rounded-lg" aria-label="Fullscreen">
              {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
